import React, { useState, useEffect, useCallback } from 'react';
import { Download, Truck, Hammer, FileText, Save, Plus, X, Archive, ClipboardCheck, FileSignature } from 'lucide-react';
import SaveQuoteModal from '@/components/SaveQuoteModal';
import SentQuotesPanel from '@/components/SentQuotesPanel';
import CompareLayoutsButton from '@/components/CompareLayoutsButton';

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * A single panel inside Appraisal Detail providing three Access-parity exports:
 *   - Per-contractor tradespack PDFs
 *   - Per-supplier material-order PDFs
 *   - Multi-tier (Estimate / Restore / Repair) proposal builder + PDF
 */
export default function ExportsPanel({ api, appraisalId, appraisalTiers, onTiersUpdated, clientEmail = '' }) {
  const [trades, setTrades] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saveQuoteOpen, setSaveQuoteOpen] = useState(false);
  const [archiveRefreshKey, setArchiveRefreshKey] = useState(0);
  const [tiers, setTiers] = useState({
    estimate: { label: 'Estimate', price: 0, summary: '', highlights: [] },
    restore:  { label: 'Restore',  price: 0, summary: '', highlights: [] },
    repair:   { label: 'Repair',   price: 0, summary: '', highlights: [] },
    ...(appraisalTiers || {}),
  });
  const [savingTiers, setSavingTiers] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        api.get(`/appraisals/${appraisalId}/tradespacks`),
        api.get(`/appraisals/${appraisalId}/supplier-orders`),
      ]);
      setTrades(t.data.trades || []);
      setSuppliers(s.data.suppliers || []);
    } catch (e) { console.error(e); }
  }, [api, appraisalId]);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (path, filename) => {
    try {
      const r = await fetch(`${REACT_APP_BACKEND_URL}/api${path}`, { credentials: 'include' });
      if (!r.ok) {
        // Surface server-side detail (e.g. "No Flashing component lines on
        // this appraisal") so the operator knows what to fix.
        let detail = `HTTP ${r.status}`;
        try {
          const j = await r.json();
          if (j?.detail) detail = j.detail;
        } catch { /* not JSON */ }
        throw new Error(detail);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Download failed: ' + e.message); }
  };

  const setTier = (k, field, v) => setTiers(prev => ({ ...prev, [k]: { ...prev[k], [field]: v } }));
  const addHighlight = (k) => setTier(k, 'highlights', [...(tiers[k].highlights || []), '']);
  const setHighlight = (k, i, v) => {
    const arr = [...(tiers[k].highlights || [])];
    arr[i] = v;
    setTier(k, 'highlights', arr);
  };
  const removeHighlight = (k, i) => {
    const arr = [...(tiers[k].highlights || [])];
    arr.splice(i, 1);
    setTier(k, 'highlights', arr);
  };

  const saveTiers = async () => {
    setSavingTiers(true);
    try {
      const { data } = await api.put(`/appraisals/${appraisalId}/tiers`, { tiers });
      onTiersUpdated?.(data.tiers);
    } catch (e) { console.error(e); }
    setSavingTiers(false);
  };

  const TIER_KEYS = [
    { k: 'estimate', tone: 'slate', accent: '#94A3B8' },
    { k: 'restore',  tone: 'orange', accent: '#D35400' },
    { k: 'repair',   tone: 'emerald', accent: '#2F855A' },
  ];

  return (
    <div data-testid="exports-panel" className="space-y-6">

      {/* Save quote to archive + download */}
      <section className="bg-white border border-[#E2E2DF] rounded-md p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-slate-900 font-[Manrope] flex items-center gap-2"><Archive size={16} className="text-[#D35400]" /> Save Quote to Client</h3>
            <p className="text-xs text-slate-500 mt-0.5">Keeps a permanent server-side copy and downloads one to your computer in a single click.</p>
          </div>
          <button
            data-testid="open-save-quote-btn"
            onClick={() => setSaveQuoteOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-sm font-semibold shadow-sm hover:shadow-md hover:shadow-orange-500/20 transition-all"
          >
            <Download size={14} strokeWidth={2.5} /> Save &amp; Download
          </button>
        </div>
        <SentQuotesPanel api={api} appraisalId={appraisalId} refreshKey={archiveRefreshKey} />
      </section>

      {/* Legacy Access-parity reports — Estimate Repair Overview + Detailed Roof Appraisal */}
      <LegacyReportsSection api={api} appraisalId={appraisalId} downloadPdf={downloadPdf} />

      {/* Tradespacks */}
      <section className="bg-white border border-[#E2E2DF] rounded-md p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-slate-900 font-[Manrope] flex items-center gap-2"><Hammer size={16} className="text-[#D35400]" /> Per-Contractor Tradespacks</h3>
            <p className="text-xs text-slate-500 mt-0.5">A printable pack each tradesperson takes to site</p>
          </div>
        </div>
        {trades.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">No quote items yet — add items in the Quote Items tab</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {trades.map(t => (
              <div key={t.trade} className="border border-[#E2E2DF] rounded-md p-3 card-lift relative overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-[3px] bg-[#D35400]" />
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold text-slate-800">{t.trade}</p>
                  <span className="text-[10px] font-mono-num text-slate-400">{t.count} items</span>
                </div>
                <p className="text-lg font-extrabold text-slate-900 font-[Manrope] font-mono-num">${t.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                <button data-testid={`tradespack-${t.trade}`} onClick={() => downloadPdf(`/appraisals/${appraisalId}/tradespack/${encodeURIComponent(t.trade)}.pdf`, `${t.trade}_tradespack.pdf`)} className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-orange-50 text-[#D35400] hover:bg-[#D35400] hover:text-white rounded-md border border-orange-100 transition-all">
                  <Download size={12} /> Download PDF
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Supplier orders */}
      <section className="bg-white border border-[#E2E2DF] rounded-md p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-slate-900 font-[Manrope] flex items-center gap-2"><Truck size={16} className="text-blue-600" /> Per-Supplier Material Orders</h3>
            <p className="text-xs text-slate-500 mt-0.5">One PDF per supplier, ready to email</p>
          </div>
        </div>
        {suppliers.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center">Pick suppliers on quote items to enable per-supplier orders</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {suppliers.map(s => (
              <div key={s.supplier} className="border border-[#E2E2DF] rounded-md p-3 card-lift relative overflow-hidden">
                <div className="absolute inset-x-0 top-0 h-[3px] bg-blue-500" />
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold text-slate-800 truncate">{s.supplier}</p>
                  <span className="text-[10px] font-mono-num text-slate-400">{s.count} items</span>
                </div>
                <p className="text-lg font-extrabold text-slate-900 font-[Manrope] font-mono-num">${s.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                <button data-testid={`supplier-order-${s.supplier}`} onClick={() => downloadPdf(`/appraisals/${appraisalId}/supplier-order.pdf?supplier=${encodeURIComponent(s.supplier)}`, `order_${s.supplier.replace(/\s+/g, '_')}.pdf`)} className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-blue-50 text-blue-700 hover:bg-blue-600 hover:text-white rounded-md border border-blue-100 transition-all">
                  <Download size={12} /> Order PDF
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Multi-tier proposal */}
      <section className="bg-white border border-[#E2E2DF] rounded-md p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-slate-900 font-[Manrope] flex items-center gap-2"><FileText size={16} className="text-emerald-600" /> Multi-Tier Proposal</h3>
            <p className="text-xs text-slate-500 mt-0.5">Three options on a single PDF — Estimate / Restore / Repair</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="save-tiers-btn" onClick={saveTiers} disabled={savingTiers} className="flex items-center gap-1.5 px-3.5 py-2 bg-[#D35400] hover:bg-[#A04000] text-white rounded-md text-xs font-semibold disabled:opacity-50 shadow-sm hover:shadow-md hover:shadow-orange-500/20 transition-all">
              <Save size={13} strokeWidth={2.5} /> {savingTiers ? 'Saving...' : 'Save Tiers'}
            </button>
            <button data-testid="proposal-pdf-btn" onClick={() => downloadPdf(`/appraisals/${appraisalId}/proposal.pdf`, 'proposal.pdf')} className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-semibold shadow-sm hover:shadow-md hover:shadow-emerald-500/20 transition-all">
              <Download size={13} strokeWidth={2.5} /> Proposal PDF
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {TIER_KEYS.map(({ k, accent }) => (
            <div key={k} className="border-2 rounded-md p-4" style={{ borderColor: accent + '40' }}>
              <div className="flex items-center justify-between mb-2">
                <input data-testid={`tier-label-${k}`} value={tiers[k].label || ''} onChange={e => setTier(k, 'label', e.target.value)} className="text-sm font-extrabold tracking-tight bg-transparent border-0 focus:ring-0 p-0 w-full" style={{ color: accent }} />
              </div>
              <div className="mb-3">
                <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">Price (incl GST)</label>
                <div className="flex items-baseline gap-1">
                  <span className="text-lg font-bold text-slate-400">$</span>
                  <input data-testid={`tier-price-${k}`} type="number" step="0.01" value={tiers[k].price || 0} onChange={e => setTier(k, 'price', parseFloat(e.target.value) || 0)} className="text-2xl font-extrabold flex-1 bg-transparent border-0 border-b border-slate-200 focus:ring-0 px-0 py-1 font-[Manrope] font-mono-num" />
                </div>
              </div>
              <div className="mb-3">
                <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">Summary</label>
                <textarea data-testid={`tier-summary-${k}`} value={tiers[k].summary || ''} onChange={e => setTier(k, 'summary', e.target.value)} rows={2} placeholder="One-sentence pitch..." className="w-full text-xs px-2 py-1.5 border border-[#E2E2DF] rounded-md mt-1" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-bold">Highlights</label>
                <div className="space-y-1 mt-1">
                  {(tiers[k].highlights || []).map((h, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <input value={h} onChange={e => setHighlight(k, i, e.target.value)} placeholder="Bullet point..." className="flex-1 text-xs px-2 py-1 border border-[#E2E2DF] rounded-md" />
                      <button type="button" onClick={() => removeHighlight(k, i)} className="p-1 text-slate-400 hover:text-rose-500"><X size={12} /></button>
                    </div>
                  ))}
                  <button type="button" onClick={() => addHighlight(k)} className="text-[11px] text-[#D35400] hover:text-[#A04000] font-semibold flex items-center gap-1 transition-colors">
                    <Plus size={11} /> Add highlight
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      <SaveQuoteModal
        open={saveQuoteOpen}
        onClose={() => setSaveQuoteOpen(false)}
        appraisalId={appraisalId}
        clientEmail={clientEmail}
        onSaved={() => setArchiveRefreshKey(k => k + 1)}
      />
    </div>
  );
}


// ---- Legacy Access-parity Reports Section ----
// Surfaces the Estimate Overview + Detailed Roof Appraisal PDFs, plus an
// inline price-override editor so the user can tune the $ amounts for one
// specific job without touching the shared per-roof-type template.
function LegacyReportsSection({ api, appraisalId, downloadPdf }) {
  const [resolved, setResolved] = useState(null); // {roof_type, template, overrides}
  const [draft, setDraft] = useState({});         // mirror of overrides while editing
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/appraisals/${appraisalId}/estimate-template`);
      setResolved(data);
      setDraft(data.overrides || {});
    } catch (e) {
      console.error(e);
      setResolved({ template: {}, overrides: {}, roof_type: '' });
    }
  }, [api, appraisalId]);
  useEffect(() => { load(); }, [load]);

  const setOverride = (k, v) => {
    setDraft(prev => {
      const next = { ...prev };
      if (v === '' || v === null || v === undefined) {
        delete next[k];
      } else {
        next[k] = v;
      }
      return next;
    });
  };

  const saveOverrides = async () => {
    setSaving(true);
    try {
      // Coerce values to numbers; blank → drop (so template default wins).
      const clean = {};
      Object.entries(draft).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        const n = parseFloat(v);
        if (!Number.isNaN(n)) clean[k] = n;
      });
      const { data } = await api.put(`/appraisals/${appraisalId}/estimate-overrides`, { overrides: clean });
      setResolved(prev => ({ ...prev, overrides: data.estimate_overrides || {} }));
      setDraft(data.estimate_overrides || {});
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save overrides');
    }
    setSaving(false);
  };

  const resetOverrides = async () => {
    if (!window.confirm('Reset all price overrides back to the template defaults?')) return;
    setSaving(true);
    try {
      await api.put(`/appraisals/${appraisalId}/estimate-overrides`, { overrides: {} });
      setResolved(prev => ({ ...prev, overrides: {} }));
      setDraft({});
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Reset failed');
    }
    setSaving(false);
  };

  const tpl = resolved?.template || {};
  const rows = tpl.estimate_rows || [];
  const dirty = JSON.stringify(draft) !== JSON.stringify(resolved?.overrides || {});

  return (
    <section data-testid="legacy-reports-section" className="bg-white border border-[#E2E2DF] rounded-md p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-bold text-slate-900 font-[Manrope] flex items-center gap-2"><FileSignature size={16} className="text-[#316536]" /> Legacy Access Reports</h3>
          <p className="text-xs text-slate-500 mt-0.5">Free Estimate Overview + Detailed Roof Appraisal — mirrors the legacy MS Access PDFs{resolved?.roof_type ? <> · roof: <span className="font-mono-num text-slate-700">{resolved.roof_type}</span> → template: <span className="font-mono-num text-slate-700">{tpl.roof_key || '(none)'}</span></> : null}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border border-[#E2E2DF] rounded-md p-4 card-lift relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-[3px] bg-[#D35400]" />
          <div className="flex items-start gap-3">
            <div className="p-2 bg-orange-50 rounded-md"><ClipboardCheck size={18} className="text-[#D35400]" /></div>
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Estimate Repair Overview</p>
              <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">Single-page free estimate with main repair options + price range. Sent before a physical inspection.</p>
            </div>
          </div>
          <button
            data-testid="estimate-overview-pdf-btn"
            onClick={() => downloadPdf(`/reports/estimate-overview/${appraisalId}`, `Estimate_Overview_${appraisalId.slice(-6)}.pdf`)}
            className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold bg-orange-50 text-[#D35400] hover:bg-[#D35400] hover:text-white rounded-md border border-orange-100 transition-all"
          >
            <Download size={12} /> Generate PDF
          </button>
        </div>
        <div className="border border-[#E2E2DF] rounded-md p-4 card-lift relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-[3px] bg-[#316536]" />
          <div className="flex items-start gap-3">
            <div className="p-2 bg-emerald-50 rounded-md"><FileSignature size={18} className="text-[#316536]" /></div>
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Roof Appraisal PDF</p>
              <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">Three variants — pick the report that matches the service the client selected.</p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 gap-1.5">
            <button
              data-testid="preview-instant-free-pdf-btn"
              onClick={() => downloadPdf(`/reports/detailed-roof-appraisal/${appraisalId}?variant=instant_free`, `Instant_Free_Report_${appraisalId.slice(-6)}.pdf`)}
              className="flex items-center justify-between gap-2 px-3 py-2 text-xs font-semibold bg-slate-50 text-slate-700 hover:bg-slate-100 rounded-md border border-slate-200 transition-all"
            >
              <span className="flex items-center gap-1.5"><Download size={11} /> Instant Free Report</span>
              <span className="text-[10px] uppercase tracking-wider text-slate-400">phone-scoped</span>
            </button>
            <button
              data-testid="preview-detailed-single-pdf-btn"
              onClick={() => downloadPdf(`/reports/detailed-roof-appraisal/${appraisalId}?variant=detailed_single`, `Detailed_1Comment_${appraisalId.slice(-6)}.pdf`)}
              className="flex items-center justify-between gap-2 px-3 py-2 text-xs font-semibold bg-amber-50 text-amber-800 hover:bg-amber-100 rounded-md border border-amber-200 transition-all"
            >
              <span className="flex items-center gap-1.5"><Download size={11} /> Detailed 1-Comment (2-page)</span>
              <span className="text-[10px] uppercase tracking-wider text-amber-500">site-visit</span>
            </button>
            <button
              data-testid="preview-detailed-multi-pdf-btn"
              onClick={() => downloadPdf(`/reports/detailed-roof-appraisal/${appraisalId}?variant=detailed_multi`, `Detailed_MultiDefect_${appraisalId.slice(-6)}.pdf`)}
              className="flex items-center justify-between gap-2 px-3 py-2 text-xs font-semibold bg-purple-50 text-purple-800 hover:bg-purple-100 rounded-md border border-purple-200 transition-all"
              title="Multi-Defect layout — bullets per area + photos. Toggle inline/gallery via Report Setup or use the Compare button below."
            >
              <span className="flex items-center gap-1.5"><Download size={11} /> Detailed Multi-Defect + Photos</span>
              <span className="text-[10px] uppercase tracking-wider text-purple-400">Multi-defect</span>
            </button>
            {/* Side-by-side visual comparison — opens M1 (inline thumbs) and
                M2 (bullets + gallery) in fresh tabs so the operator can
                pick the preferred layout without editing the template. */}
            <div className="pt-1">
              <CompareLayoutsButton appraisalId={appraisalId} variant="detailed_multi" size="compact" />
            </div>
          </div>
        </div>
        {/* Combined supplier order PDF — pulls every Flashing line on the
            appraisal, draws its side-profile, and totals the sheet area. */}
        <div className="border border-[#E2E2DF] rounded-md p-4 card-lift relative overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-[3px] bg-[#16A34A]" />
          <div className="flex items-start gap-3">
            <div className="p-2 bg-emerald-50 rounded-md"><FileSignature size={18} className="text-emerald-700" /></div>
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Flashing Supplier Order</p>
              <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed">Combined order sheet — one row per Flashing component with side-profile, dimensions, quantity and a totals page.</p>
            </div>
          </div>
          <button
            data-testid="flashing-supplier-order-pdf-btn"
            onClick={() => downloadPdf(`/reports/flashing-supplier-order/${appraisalId}`, `Flashing_Supplier_Order_${appraisalId.slice(-6)}.pdf`)}
            className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold bg-emerald-50 text-emerald-700 hover:bg-emerald-700 hover:text-white rounded-md border border-emerald-100 transition-all"
          >
            <Download size={12} /> Generate PDF
          </button>
        </div>
      </div>

      {/* Per-appraisal price overrides */}
      {rows.length > 0 && (
        <div data-testid="estimate-overrides-block" className="mt-5 border-t border-[#F0EFEC] pt-4">
          <div className="flex items-center justify-between mb-2.5">
            <div>
              <p className="text-xs font-bold text-slate-800">Estimate prices for this job</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Leave a field blank to use the template default. Edits only affect this appraisal — not the shared template.</p>
            </div>
            <div className="flex items-center gap-2">
              {savedAt && <span className="text-[11px] text-emerald-600 font-semibold">Saved ✓</span>}
              <button data-testid="reset-overrides-btn" onClick={resetOverrides} disabled={saving || Object.keys(resolved?.overrides || {}).length === 0} className="px-2.5 py-1.5 border border-[#E2E2DF] text-xs text-slate-600 rounded-md hover:bg-slate-50 disabled:opacity-40">Reset to defaults</button>
              <button data-testid="save-overrides-btn" onClick={saveOverrides} disabled={saving || !dirty} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D35400] text-white rounded-md text-xs font-semibold hover:bg-[#A04000] disabled:opacity-50">
                <Save size={12} strokeWidth={2.5} /> {saving ? 'Saving…' : 'Save overrides'}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {rows.map(r => {
              const overridden = draft[r.key] !== undefined && draft[r.key] !== '';
              return (
                <div key={r.key} className={`flex items-center gap-2 px-2.5 py-2 border rounded-md transition-colors ${overridden ? 'border-[#D35400] bg-orange-50/40' : 'border-[#E2E2DF] bg-white'}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-700 truncate">{r.label}</p>
                    <p className="text-[10px] text-slate-400 font-mono-num">default ${(r.default || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-400">$</span>
                    <input
                      data-testid={`override-${r.key}`}
                      type="number"
                      step="0.01"
                      placeholder={r.default ?? 0}
                      value={draft[r.key] ?? ''}
                      onChange={e => setOverride(r.key, e.target.value)}
                      className="w-24 text-right px-2 py-1 border border-[#E2E2DF] rounded text-sm font-mono-num focus:outline-none focus:border-[#D35400]"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
